import { t } from "i18next";
import { DateTime } from "luxon";
import { DateRange } from "react-day-picker";
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { TbTrash } from "react-icons/tb";
import MultiShiftPicker from "../../../components/Filters/MultiShiftPicker";
import { useAppStore } from "../../../stores/RootStore";
import { Tab, useTabsStore } from "../../../stores/TabsStore";
import { FShift } from "../../../types/types";
import { Button } from "../../../ui/Button";
import { Calendar } from "../../../ui/Calendar";
import { Label } from "../../../ui/Label/Label";
import MultipleSelector from "../../../ui/MultipleSelector/MultipleSelector";
import { ScrollArea } from "../../../ui/ScrollArea/ScrollArea";
import TimePicker from "../../../ui/TimePicker/TimePicker";
import { Input } from "../../../ui/Input/Input";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/ToggleGroup/ToggleGroup";
import HelpPopover from "../../../ui/HelpPopover/HelpPopover";
import OneSelector, { Option } from "../../../ui/OneSelector/OneSelector";
import { templates } from "../../../components/Templates/templates";
import { Separator } from "../../../ui/Separator";
import { ReportViewButton } from "./ReportViewButton";
import { ChangeEvent } from "react";

type Props = {
  tab: Tab;
};

const ReportForm = ({ tab }: Props) => {
  const methods = useForm({
    defaultValues: {
      title: tab.title ? tab.title : t('report_by_persons'),
      template: tab.template ? tab.template : "persons_free_template",
      defaultValues: {
        period_type: tab!.defaultValues?.period_type ? tab!.defaultValues?.period_type : "static",
        dates: {
          from: tab!.defaultValues?.dates?.from
            ? tab.defaultValues?.dates?.from
            : DateTime.local().toJSDate(),
          to: tab!.defaultValues?.dates?.to
            ? tab!.defaultValues?.dates?.to
            : DateTime.local().toJSDate(),
        },
        dynamic_period: tab!.defaultValues?.dynamic_period ? tab!.defaultValues?.dynamic_period : 1,
        persons: tab?.defaultValues?.persons?.length! > 0 ? tab.defaultValues?.persons : [],
        shifts: tab?.defaultValues?.shifts?.length! > 0 ? tab.defaultValues?.shifts! : [],
        excludes: tab?.defaultValues?.excludes?.length! > 0 ? tab.defaultValues?.excludes! : [],
      },
    },
  });
  const { handleSubmit, control, watch, setValue, register } = methods;
  const templatesList: Option[] = templates
    .filter((t) => t.tabType === "persons")
    .map((t) => ({ label: t.name, value: t.slug }));
  const personsData = useAppStore().persons;
  const persons = personsData?.map((p) => {
    return {
      value: p.id,
      label: `${p.attributes.last_name} ${p.attributes.first_name} ${p.attributes.middle_name}`,
      group: p.attributes.division_name ? p.attributes.division_name : t("no_division"),
    };
  });

  const { addDefaultValues, deleteCurrentData, changeTabTemplate, changeTabTitle } = useTabsStore();

  const onSubmit = (formData: any) => {
    if (!formData?.defaultValues.dates?.to) return;

    // Очистка данных при новом запросе через settings
    if (tab.defaultValues) deleteCurrentData(tab.id);

    // Проверка и назначение смен для перерывов
    let defaultValues = formData.defaultValues;
    const shifts = defaultValues.shifts as unknown as FShift[];

    let excludes = defaultValues.excludes as unknown as {
      exclude_start: DateTime;
      exclude_end: DateTime;
      shift: string;
    }[];
    // Дополнительная валидация и маркировка перерыва номером смены
    for (let exclude of excludes) {
      const excludeStartInSeconds = exclude.exclude_start.toSeconds() - exclude.exclude_start.startOf("day").toSeconds();
      const excludeEndInSeconds = exclude.exclude_end.toSeconds() - exclude.exclude_end.startOf("day").toSeconds();
      for (let shift of shifts) {
        const shiftStartInSeconds = shift.start_at.toSeconds() - shift.start_at.startOf("day").toSeconds();
        const shiftEndInSeconds = shift.end_at.toSeconds() - shift.end_at.startOf("day").toSeconds();
        if (shift.start_at.toSeconds() < shift.end_at.toSeconds()) {
          if (
            excludeStartInSeconds >= shiftStartInSeconds &&
            excludeStartInSeconds < shiftEndInSeconds &&
            excludeEndInSeconds > shiftStartInSeconds &&
            excludeEndInSeconds <= shiftEndInSeconds
          )
            exclude.shift = shift.id;
        } else {
          if (
            (excludeStartInSeconds >= shiftStartInSeconds || excludeStartInSeconds < shiftEndInSeconds) &&
            (excludeEndInSeconds > shiftStartInSeconds || excludeEndInSeconds <= shiftEndInSeconds)
          )
            exclude.shift = shift.id;
        }
      }
      if (!exclude.shift) exclude.shift = "invalid";
    }
    const formExcludes = excludes.filter((exclude) => exclude.shift != "invalid");

    let excludesList: {
      exclude_start: DateTime;
      exclude_end: DateTime;
      shift: string;
    }[] = [...formExcludes];
    for (let shift of shifts) {
      let breaks = shift.working_shift_breaks.map((shiftBreak) => {
        return {
          exclude_start: DateTime.fromISO(shiftBreak.started_at),
          exclude_end: DateTime.fromISO(shiftBreak.ended_at),
          shift: shift.id,
        };
      });
      excludesList = [...excludesList, ...breaks];
    }

    defaultValues.excludes = excludesList;
    changeTabTitle(tab.id, formData?.title)
    changeTabTemplate(tab.id, formData?.template);
    addDefaultValues(tab.id, defaultValues);
  };

  function handleSelectDate(v: DateRange | undefined, onChange: (...event: any[]) => void) {
    onChange(v);
  }

  function handleChangeTitle(e: ChangeEvent<HTMLInputElement>, onChange: (...event: any[]) => void) {
    onChange(e.target.value)
  }

  return (
    <>
      <div className="flex flex-none h-12 w-full items-center px-2 gap-2 justify-between">
        <div className="flex gap-2">
          <ReportViewButton />
        </div>
      </div>
      <Separator />
      <FormProvider {...methods}>
        <ScrollArea className="h-full">
          <form onSubmit={handleSubmit(onSubmit)} className="w-full h-full max-w-min mx-auto">
            <div className="flex flex-col gap-3 my-10 w-[550px]">
              <div className="text-xl font-semibold">Настройки для отчета</div>
              <div className="relative space-y-2">
                <Label>{t("report_title")}</Label>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <Input value={value} onChange={(e) => handleChangeTitle(e, onChange)} placeholder={t('enter_report_title')}/>
                    );
                  }}
                />
              </div>
              <div className="relative space-y-2">
                <Label>
                  <HelpPopover
                    className="justify-between w-full"
                    title={t("choose_date")}
                    helpText={t("choose_date_desc")}
                    align="end"
                  ></HelpPopover>
                </Label>
                <div className="border rounded-sm">
                  <Controller
                    name="defaultValues.dates"
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <Calendar
                          initialFocus
                          mode="range"
                          defaultMonth={DateTime.fromJSDate(value?.from).minus({ month: 1 }).toJSDate()}
                          selected={value}
                          onSelect={(v) => handleSelectDate(v, onChange)}
                          numberOfMonths={2}
                          disabled={{
                            after: DateTime.local().toJSDate(),
                          }}
                        />
                      );
                    }}
                  />
                </div>
              </div>
              <div className="relative space-y-2">
                <Label>
                  <HelpPopover
                    className="justify-between w-full"
                    title={t("persons")}
                    helpText={t("report_by_persons_desc")}
                    align="end"
                  ></HelpPopover>
                </Label>
                <Controller
                  name="defaultValues.persons"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <MultipleSelector
                      value={field.value}
                      onChange={field.onChange}
                      defaultOptions={persons}
                      groupBy="group"
                    />
                  )}
                />
              </div>
              <div className="relative space-y-2 flex flex-col w-full">
                <Label>
                  <HelpPopover
                    className="justify-between w-full"
                    title={t("shifts")}
                    helpText={t("report_shifts_desc")}
                    align="end"
                  ></HelpPopover>
                </Label>
                <Controller
                  name="defaultValues.shifts"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <MultiShiftPicker chosenShifts={field.value!} setChosenShifts={field.onChange} />
                  )}
                />
              </div>
              {watch("defaultValues.shifts").length > 0 && <ExcludesPicker />}
              <Button disabled={tab.dataIsLoading} type="submit" size="default" variant="default">
                {t("configure")}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </FormProvider>
    </>
  );
};

export default ReportForm;

// Компонент для выбора перерывов
export const ExcludesPicker = () => {
  const methods = useFormContext();
  const {
    control,
    formState: { errors },
    getValues,
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "defaultValues.excludes",
  });

  return (
    <div className="grid gap-4">
      <div className="space-y-2">
        <h4 className="font-medium text-sm leading-none">Перерывы</h4>
        <div className="flex w-full justify-between items-center">
          <p className="text-sm text-muted-foreground">Добавление перерывов</p>
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() =>
              append({ exclude_start: DateTime.now().startOf("day"), exclude_end: DateTime.now().startOf("day") })
            }
          >
            Добавить перерыв
          </Button>
        </div>
      </div>
      <div className="grid gap-2">
        {fields.map((item, index) => (
          <div key={item.id} className="flex justify-between items-center">
            <>
              {/* {errors?.excludes! && errors?.excludes[index]! ? errors?.excludes[index]?.excludeEnd?.message : ""} */}
              <Controller
                control={control}
                name={`defaultValues.excludes.${index}.exclude_start`}
                rules={{
                  validate: (value, formValues) => {
                    return validateExclude(formValues.defaultValues.excludes, getValues("defaultValues.shifts"), index);
                  },
                }}
                render={({ field: { value, onChange }, formState }) => <TimePicker value={value} onChange={onChange} />}
              />
              <Controller
                control={control}
                name={`defaultValues.excludes.${index}.exclude_end`}
                rules={{
                  validate: (value, formValues) => {
                    return validateExclude(formValues.defaultValues.excludes, getValues("defaultValues.shifts"), index);
                  },
                }}
                render={({ field: { value, onChange } }) => <TimePicker value={value} onChange={onChange} />}
              />
              <Button type="button" variant="destructive" size="xs" onClick={() => remove(index)}>
                <TbTrash />
              </Button>
            </>
          </div>
        ))}
      </div>
    </div>
  );
};

// Функция для валидации выбора перерыва
const validateExclude = (
  excludes: { exclude_start: DateTime; exclude_end: DateTime }[],
  chosenShifts: FShift[],
  index: number
) => {
  if (chosenShifts.length == 0) return "Ошибка: смены не выбраны";
  let validation = false;
  const excludeStartInSeconds =
    excludes[index].exclude_start.toSeconds() - excludes[index].exclude_start.startOf("day").toSeconds();
  const excludeEndInSeconds =
    excludes[index].exclude_end.toSeconds() - excludes[index].exclude_end.startOf("day").toSeconds();
  for (let shift of chosenShifts) {
    const shiftStartInSeconds = shift.start_at.toSeconds() - shift.start_at.startOf("day").toSeconds();
    const shiftEndInSeconds = shift.end_at.toSeconds() - shift.end_at.startOf("day").toSeconds();
    if (shiftStartInSeconds < shiftEndInSeconds) {
      if (
        excludeStartInSeconds >= shiftStartInSeconds &&
        excludeStartInSeconds < shiftEndInSeconds &&
        excludeEndInSeconds > shiftStartInSeconds &&
        excludeEndInSeconds <= shiftEndInSeconds
      ) {
        validation = true;
      }
    } else {
      if (
        (excludeStartInSeconds >= shiftStartInSeconds || excludeStartInSeconds < shiftEndInSeconds) &&
        (excludeEndInSeconds > shiftStartInSeconds || excludeEndInSeconds <= shiftEndInSeconds)
      ) {
        validation = true;
      }
    }
  }

  return validation || "Ошибка: перерыв не входит в интервал смен";
};
